import React from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { Link } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  API_URL_DEPARTMENT_INACTIVITY,
  dateTimeFormatter,
} from '../configuration/Configuration';
import { exportDepartmentInactivityToExcel } from '../configuration/Utils';

const API_DATE_FORMAT = 'YYYY-MM-DD';
const PRESET_DAYS = [30, 90, 180, 365];
const DEFAULT_DAYS = 90;

const daysSince = (value) => (value ? dayjs().diff(dayjs(value), 'day') : null);

const columnDefs = [
  {
    field: 'department',
    headerName: 'Department',
    cellRenderer: 'agGroupCellRenderer',
    flex: 2,
    minWidth: 200,
  },
  { field: 'received', headerName: 'Received', flex: 1 },
  { field: 'reacted', headerName: 'Reacted', flex: 1 },
  { field: 'missed', headerName: 'Missed', flex: 1, sort: 'desc' },
  { field: 'pending', headerName: 'Pending', flex: 1 },
  {
    field: 'response_rate',
    headerName: 'Response Rate',
    flex: 1,
    valueFormatter: (params) =>
      params.value === null || params.value === undefined
        ? '–'
        : `${Math.round(params.value * 100)} %`,
  },
  {
    field: 'last_reaction_at',
    headerName: 'Last Reaction',
    flex: 1.5,
    valueFormatter: (params) => dateTimeFormatter(params) || 'Never',
  },
  {
    colId: 'days_since_last_reaction',
    headerName: 'Days Since Last Reaction',
    flex: 1.5,
    valueGetter: (params) => daysSince(params.data.last_reaction_at),
    valueFormatter: (params) => (params.value === null ? '–' : params.value),
  },
];

const TenderLinkRenderer = (params) => (
  <Link to={`/AdvanceTender?tender_id=${params.value}`}>{params.value}</Link>
);

const detailCellRendererParams = {
  detailGridOptions: {
    columnDefs: [
      {
        field: 'tender_id',
        headerName: 'Tender ID',
        cellRenderer: TenderLinkRenderer,
        flex: 1,
      },
      { field: 'company', headerName: 'Company', flex: 2 },
      { field: 'tender_status', headerName: 'Status', flex: 1.5 },
      {
        field: 'sent_at',
        headerName: 'Sent',
        valueFormatter: dateTimeFormatter,
        flex: 1.5,
      },
      {
        field: 'internal_deadline',
        headerName: 'Internal Deadline',
        valueFormatter: dateTimeFormatter,
        flex: 1.5,
      },
    ],
  },
  getDetailRowData: (params) =>
    params.successCallback(params.data.missed_tenders),
};

const rowClassRules = {
  'inactive-department-row': (params) =>
    params.data.missed > 0 && params.data.reacted === 0,
};

const Analytics = () => {
  const [start, setStart] = React.useState(
    dayjs().subtract(DEFAULT_DAYS, 'day')
  );
  const [end, setEnd] = React.useState(dayjs());
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const rangeIsValid =
    Boolean(start?.isValid() && end?.isValid()) && !start.isAfter(end, 'day');
  const startParam = rangeIsValid ? start.format(API_DATE_FORMAT) : null;
  const endParam = rangeIsValid ? end.format(API_DATE_FORMAT) : null;

  React.useEffect(() => {
    if (!startParam || !endParam) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(API_URL_DEPARTMENT_INACTIVITY, {
        params: { start: startParam, end: endParam },
      })
      .then((response) => {
        if (!cancelled) setRows(response.data.departments);
      })
      .catch((err) => {
        if (!cancelled) {
          setRows([]);
          setError(
            err.response?.data?.error || 'Could not load department activity.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startParam, endParam]);

  const applyPreset = (days) => {
    setStart(dayjs().subtract(days, 'day'));
    setEnd(dayjs());
  };

  const withMissed = rows.filter((row) => row.missed > 0).length;
  const fullyInactive = rows.filter(
    (row) => row.missed > 0 && row.reacted === 0
  ).length;

  return (
    <Stack spacing={2} alignItems="stretch">
      <Typography variant="h6" align="left">
        Department Activity
      </Typography>
      <Typography variant="body2" align="left" color="text.secondary">
        Tenders sent to each department in the selected period. A department
        reacted if it viewed the tender, declined it, confirmed prices or
        uploaded a document. Unanswered tenders count as missed once the
        internal deadline has passed or the tender is closed, and as pending
        before that.
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <DatePicker
            label="From"
            value={start}
            onChange={setStart}
            format="DD.MM.YYYY"
          />
          <DatePicker
            label="To"
            value={end}
            onChange={setEnd}
            format="DD.MM.YYYY"
          />
        </LocalizationProvider>
        <ButtonGroup variant="outlined" size="small">
          {PRESET_DAYS.map((days) => (
            <Button key={days} onClick={() => applyPreset(days)}>
              {days} days
            </Button>
          ))}
        </ButtonGroup>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={!rows.length || loading || !rangeIsValid}
          onClick={() =>
            exportDepartmentInactivityToExcel(rows, startParam, endParam)
          }
        >
          Export to Excel
        </Button>
        {loading && <CircularProgress size={24} />}
      </Stack>

      {!rangeIsValid && (
        <Alert severity="warning">
          The start date must not be after the end date.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {rangeIsValid && !error && (
        <Typography align="left">
          {withMissed} of {rows.length} departments missed at least one tender;{' '}
          {fullyInactive} did not react to any tender they received.
        </Typography>
      )}

      <div className="ag-theme-alpine" style={{ width: '100%' }}>
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          getRowId={(params) => String(params.data.department_id)}
          masterDetail={true}
          isRowMaster={(data) => data.missed_tenders.length > 0}
          detailCellRendererParams={detailCellRendererParams}
          detailRowHeight={320}
          rowClassRules={rowClassRules}
          domLayout="autoHeight"
        />
      </div>
    </Stack>
  );
};

export default Analytics;
